import { differenceInMinutes, format, isSameDay, parseISO, startOfDay } from 'date-fns';
import { nl } from 'date-fns/locale';

import type {
    InspectionRecord,
    OrderRecord,
    PhotoRecord,
    TemplateVersionRecord,
} from '@/shared/offline';

/**
 * Mijn dag uit de lokale opslag: de opdrachten die `/sync/bootstrap` voor
 * deze inspecteur klaarzette, ook zonder netwerk. Puur - de pagina leest de
 * records en rekent hiermee.
 *
 * Alleen de eigen stops. Een inspecteur krijgt alleen zijn eigen opdrachten op
 * het apparaat, maar een eigenaar die ook keurt krijgt alle
 * opdrachten van het bedrijf; `assigneeUuid` zegt welke van hem zijn.
 *
 * Elke eigen afspraak is een stop, ook een keuring zonder opname, zoals een
 * energielabel zonder vragenlijst: ook dat is een bezoek ter plaatse. De opname
 * beslist alleen wat de stop start en of hij meetelt voor wat er offline
 * klaarstaat. Een stop start altijd een opname met vragenlijst, nooit die van de
 * NHG-bijlage. Zie {@link surveyOf}.
 */

export interface LocalStop {
    orderUuid: string;
    /** De opname die deze stop start; `null` bij een keuring zonder opname, zoals een energielabel zonder vragenlijst. */
    inspectionUuid: string | null;
    orderNumber: string;
    /** `09:00` */
    time: string | null;
    /** `do 17 sep` - alleen voor een stop die niet vandaag is. */
    date: string | null;
    durationMinutes: number | null;
    /** `NTA 8060` */
    normLabel: string;
    address: string | null;
    clientName: string | null;
    /** Een energielabel gaat naar het NTA 8800-scherm, de rest naar de opname. */
    energyLabel: boolean;
    /** De opname loopt al: "Opname hervatten". */
    resuming: boolean;
    /** Ingediend: alleen nog te bekijken. */
    submitted: boolean;
    /** Opname en vragenlijst staan op dit apparaat. */
    ready: boolean;
    scheduledAt: string | null;
}

export interface LocalDay {
    today: LocalStop[];
    upcoming: LocalStop[];
    /**
     * Wat er offline klaarstaat. `total` en `ready` tellen alleen de stops met een opname;
     * `withoutSurvey` zijn de afspraken waarvoor niets op dit apparaat hoeft te staan.
     */
    offline: { ready: number; total: number; withoutSurvey: number; bytes: number };
}

const ENERGY_LABEL_CODES = new Set(['nta8800', 'energielabel']);

/** `nta8060` → `NTA 8060`; zonder code het label van de dienst. */
export function normLabelFor(order: OrderRecord): string {
    const match = /^([a-z]+)\s*(\d+)$/i.exec(order.serviceCode ?? '');

    if (match) {
        return `${match[1]?.toUpperCase()} ${match[2]}`;
    }

    return order.serviceLabel ?? order.serviceCode ?? '';
}

export function addressOf(order: OrderRecord): string | null {
    const address = order.address;

    if (!address) {
        return null;
    }

    const street = [
        address.street,
        `${address.houseNumber ?? ''}${address.houseNumberSuffix ?? ''}`,
    ]
        .filter((part) => part && part.trim() !== '')
        .join(' ');
    const line = [street, address.city].filter(Boolean).join(', ');

    return line === '' ? null : line;
}

/**
 * Is dit een stop van deze persoon? Een opdracht die is opgeslagen voordat de
 * server `assigned_user_uuid` meestuurde, zegt dat niet; die blijft staan, zoals
 * voorheen, tot de volgende bootstrap hem aanvult.
 */
export function isOwnStop(order: OrderRecord, userUuid: string | null): boolean {
    if (order.assigneeUuid === undefined) {
        return true;
    }

    return userUuid !== null && order.assigneeUuid === userUuid;
}

/**
 * De opname die een stop start, of `null` als de keuring er geen heeft: dan is de stop
 * alleen een afspraak.
 *
 * De server wijst hem aan (`order.inspectionUuid`). Een opdracht die is bewaard voordat
 * de server alleen opnames met vragenlijst aanwees, kan nog naar de opname van de
 * NHG-bijlage wijzen; dan telt de opname met vragenlijst van dezelfde keuring. Staat de
 * aangewezen opname (nog) niet op dit apparaat, dan blijft de stop staan: het scherm zegt
 * dan dat hij niet op dit apparaat staat.
 */
export function surveyOf(
    order: OrderRecord,
    inspections: ReadonlyMap<string, InspectionRecord>,
    inspectionsOfOrder: readonly InspectionRecord[],
): { uuid: string; record: InspectionRecord | undefined } | null {
    const pointed = order.inspectionUuid ? inspections.get(order.inspectionUuid) : undefined;

    if (pointed && pointed.templateVersionUuid !== null) {
        return { uuid: pointed.uuid, record: pointed };
    }

    const survey = inspectionsOfOrder.find((row) => row.templateVersionUuid !== null);

    if (survey) {
        return { uuid: survey.uuid, record: survey };
    }

    if (order.inspectionUuid && pointed === undefined) {
        return { uuid: order.inspectionUuid, record: undefined };
    }

    return null;
}

export function buildLocalDay(input: {
    orders: readonly OrderRecord[];
    inspections: readonly InspectionRecord[];
    versions: readonly TemplateVersionRecord[];
    photos: readonly Pick<PhotoRecord, 'byteSize' | 'blob'>[];
    now: Date;
    /** De persoon van wie deze opslag is (`db.scope.userUuid`). */
    userUuid: string | null;
}): LocalDay {
    const inspections = new Map(input.inspections.map((row) => [row.uuid, row]));
    const inspectionsByOrder = new Map<string, InspectionRecord[]>();
    const versions = new Map(input.versions.map((row) => [row.uuid, row]));
    const todayStart = startOfDay(input.now);

    for (const row of input.inspections) {
        if (row.orderUuid) {
            inspectionsByOrder.set(row.orderUuid, [
                ...(inspectionsByOrder.get(row.orderUuid) ?? []),
                row,
            ]);
        }
    }

    const stops = input.orders
        .filter((order) => isOwnStop(order, input.userUuid))
        .map((order): LocalStop => {
            const survey = surveyOf(order, inspections, inspectionsByOrder.get(order.uuid) ?? []);
            const inspection = survey?.record;
            const version = inspection?.templateVersionUuid
                ? versions.get(inspection.templateVersionUuid)
                : undefined;
            const start = order.scheduledAt ? parseISO(order.scheduledAt) : null;
            const end = order.scheduledEndAt ? parseISO(order.scheduledEndAt) : null;
            const today = start ? isSameDay(start, input.now) : false;

            return {
                orderUuid: order.uuid,
                inspectionUuid: survey?.uuid ?? null,
                orderNumber: order.number,
                time: start ? format(start, 'HH:mm') : null,
                date: start && !today ? format(start, 'EEE d MMM', { locale: nl }) : null,
                durationMinutes: start && end ? Math.max(0, differenceInMinutes(end, start)) : null,
                normLabel: normLabelFor(order),
                address: addressOf(order),
                clientName: order.clientName,
                /* De dienst van de opdracht is die van de aangewezen opname. */
                energyLabel:
                    survey !== null &&
                    survey.uuid === order.inspectionUuid &&
                    ENERGY_LABEL_CODES.has(order.serviceCode ?? ''),
                resuming:
                    inspection?.status === 'in_progress' || inspection?.status === 'completed',
                submitted: inspection?.status === 'submitted',
                ready: Boolean(inspection && version?.fetchedAt),
                scheduledAt: order.scheduledAt,
            };
        });

    const byTime = (a: LocalStop, b: LocalStop) =>
        (a.scheduledAt ?? '').localeCompare(b.scheduledAt ?? '');
    const isToday = (stop: LocalStop) =>
        stop.scheduledAt !== null && isSameDay(parseISO(stop.scheduledAt), input.now);

    /* Vandaag, plus een opname die nog loopt van een eerdere dag. */
    const today = stops
        .filter((stop) => isToday(stop) || (stop.resuming && !isFuture(stop, todayStart)))
        .sort(byTime);
    const todayUuids = new Set(today.map((stop) => stop.orderUuid));
    const upcoming = stops
        .filter((stop) => !todayUuids.has(stop.orderUuid) && isFuture(stop, todayStart))
        .sort(byTime);

    const bytes =
        input.versions.reduce(
            (total, version) => total + (version.fetchedAt ? version.byteSize : 0),
            0,
        ) + input.photos.reduce((total, photo) => total + (photo.blob ? photo.byteSize : 0), 0);

    return {
        today,
        upcoming,
        offline: {
            ready: stops.filter((stop) => stop.ready).length,
            total: stops.filter((stop) => stop.inspectionUuid !== null).length,
            withoutSurvey: stops.filter((stop) => stop.inspectionUuid === null).length,
            bytes,
        },
    };
}

function isFuture(stop: LocalStop, todayStart: Date): boolean {
    return stop.scheduledAt !== null && parseISO(stop.scheduledAt) >= todayStart;
}
